import { useCallback, useEffect, useState } from 'react';
import { RouterProvider, type RegisteredRouter } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, getMeta } from './db/dexie.js';
import { accesoDeDesarrollo } from './lib/acceso.js';
import { puedeHaberVersionNueva } from './lib/actualizacion.js';
import { hayQuePedirPin, ultimoUso } from './lib/bloqueo.js';
import { enPractica } from './lib/practica.js';
import { prepararPractica } from './lib/practica-montar.js';
import {
  alCambiarUsuario,
  bloquear,
  estabaBloqueada,
  minutosSinUso,
  restaurarSesion,
  useBloqueada,
  useSesion,
  vigilarUso,
} from './lib/sesion.js';
import { iniciarSyncAutomatica, refrescarContadores } from './sync/engine.js';
import { iniciarEscucha } from './sync/escucha.js';
import { traducir } from './i18n/index.js';
import { Instalar } from './routes/Instalar.js';
import { ActualizacionDisponible } from './ui/ActualizacionDisponible.js';
import { Hecho } from './ui/Hecho.js';
import { DadoDeBaja } from './ui/DadoDeBaja.js';
import { Puerta } from './ui/Puerta.js';

type Fase = 'cargando' | 'instalar' | 'app';

/**
 * Four gates, in order: is this device registered, is somebody signed in, is this the newest
 * version, and only then the app. The first two resolve from IndexedDB, so a cold start with no
 * connectivity lands on the PIN pad rather than a spinner waiting on a network that is not there,
 * and the third gives up on such a network within seconds (`lib/actualizacion.ts`).
 *
 * The one exception is an unregistered device talking to a development server, which enrols
 * itself instead of asking. A laptop has no need for the ceremony that keeps two offline
 * terminals from issuing the same receipt number.
 *
 * And over the app, once it has gone unused for a while or somebody pressed «Bloquear», the PIN
 * pad of whoever was signed in (see `lib/bloqueo.ts`). The practice shop only locks when asked:
 * it has nothing to protect, and a lesson that should not be interrupted.
 *
 * The router is handed in by `main.tsx` rather than imported here; that file says why.
 */
export function App({ router }: { router: RegisteredRouter }) {
  const [fase, setFase] = useState<Fase>('cargando');
  const sesion = useSesion();
  const bloqueada = useBloqueada();
  const dadoDeBaja = useLiveQuery(() => getMeta('dadoDeBaja'), [], undefined);
  // Their own row, live: shut out or given a new role elsewhere, it applies here at once.
  const yo = useLiveQuery(
    () => (sesion ? db.usuarios.get(sesion.usuarioId) : undefined),
    [sesion?.usuarioId],
  );
  useEffect(() => {
    if (yo) void alCambiarUsuario(yo);
  }, [yo]);

  /*
   * The PIN pad is a door over the app (see `Puerta`), and once the PIN is right it stays up a
   * moment longer to dissolve into it. Tracked here, during render, so that the door is the
   * same element from the moment it is needed until it has finished opening — and a new one,
   * by its key, each time it is needed again.
   *
   * Past the PIN, or with none to ask for, it stays shut until the app has checked for a new
   * version (`lib/actualizacion.ts`): at every start, a reload included, so that reloading is no
   * way round it, and each time the PIN pad comes back. Where no service worker runs there is
   * nothing to check, and no door for it.
   */
  const puertaNecesaria = fase === 'app' && (!sesion || bloqueada);
  const [revisada, setRevisada] = useState(() => !puedeHaberVersionNueva());
  const [pedia, setPedia] = useState(puertaNecesaria);
  if (pedia !== puertaNecesaria) {
    setPedia(puertaNecesaria);
    if (puertaNecesaria) setRevisada(!puedeHaberVersionNueva());
  }
  const cerrada = puertaNecesaria || (fase === 'app' && !revisada);
  const [antes, setAntes] = useState(cerrada);
  const [abriendose, setAbriendose] = useState(false);
  const [vuelta, setVuelta] = useState(0);
  if (antes !== cerrada) {
    setAntes(cerrada);
    setAbriendose(!cerrada);
    if (cerrada) setVuelta((v) => v + 1);
  }

  const arrancar = useCallback(async () => {
    /**
     * A practice session builds its own shop and never asks the server for anything — no
     * token, no enrolment, no install screen. It cannot reach the network by design, so any
     * path that waits on one would just hang here.
     */
    if (enPractica()) {
      await prepararPractica();
      await restaurarSesion();
      if (estabaBloqueada()) bloquear();
      await refrescarContadores();
      setFase('app');
      return;
    }

    // Read before anything is awaited, so a tap on the loading screen cannot count as use. How
    // long is too long is the shop's setting, which takes an await to read, so that part waits.
    const yaBloqueada = estabaBloqueada();
    const ultimo = ultimoUso();
    const ahora = Date.now();

    let token = await getMeta('token');
    if (!token) {
      // Development servers let the app in by itself; everywhere else this is false and the
      // install screen appears exactly as before.
      if (await accesoDeDesarrollo()) token = await getMeta('token');
      if (!token) {
        setFase('instalar');
        return;
      }
    }
    await restaurarSesion();
    if (yaBloqueada || hayQuePedirPin(ultimo, ahora, await minutosSinUso())) bloquear();
    await refrescarContadores();
    setFase('app');
  }, []);

  useEffect(() => {
    void arrancar();
  }, [arrancar]);

  // From the first screen, so the taps that install the app count as using it.
  useEffect(() => {
    if (enPractica()) return;
    return vigilarUso();
  }, []);

  // Whoever signs in next starts at the beginning, not on the screen somebody signed out from.
  useEffect(() => {
    if (fase === 'app' && !sesion && router.state.location.pathname !== '/') {
      void router.navigate({ to: '/', replace: true, viewTransition: false });
    }
  }, [fase, sesion, router]);

  useEffect(() => {
    if (fase !== 'app') return;
    const parar = iniciarSyncAutomatica();
    // And the server's word the moment the log grows, between the polls (`sync/escucha.ts`).
    const dejarDeEscuchar = iniciarEscucha();
    return () => {
      parar();
      dejarDeEscuchar();
    };
  }, [fase]);

  if (fase === 'cargando') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-sm text-slate-500">{traducir('comun.abriendo')}</p>
      </div>
    );
  }

  if (fase === 'instalar') return <Instalar onListo={() => void arrancar()} />;

  if (dadoDeBaja) return <DadoDeBaja />;

  return (
    <>
      {sesion && <RouterProvider router={router} />}
      {sesion && <ActualizacionDisponible />}
      {sesion && <Hecho />}
      {(cerrada || abriendose) && (
        <Puerta
          key={vuelta}
          saliendo={!cerrada}
          pidePin={puertaNecesaria}
          onAlDia={() => setRevisada(true)}
          onFuera={() => setAbriendose(false)}
          onOtraPersona={() => void router.navigate({ to: '/', viewTransition: false })}
        />
      )}
    </>
  );
}
